import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Divider, Grid, Paper as MuiPaper, TablePagination } from '@mui/material';
import { spacing } from '@mui/system';
import styled from '@emotion/styled';

import { getInitialTableState, useServiceHistoryStore } from '../../../stores/serviceHistoryStore';
import { useSessionStore } from '../../../stores';
import { RouteStopListItem, RouteStopListRequest } from '../../../apis/serviceHistoryApi';
import ServiceHistoryCard from './ServiceHistoryCard';
import EmptyResults from '../../../components/common/EmptyResults';
import ServiceHistoryFilterBar from './ServiceHistoryFilterBar';

const Paper = styled(MuiPaper)(spacing);

function ServiceHistoryCardList() {
  const [searchParams] = useSearchParams();
  const fromDetail = !!searchParams.get('fromDetail');
  const initialTableState = getInitialTableState();

  const tableState = useServiceHistoryStore.use.tableState();
  const setStoredPage = useServiceHistoryStore.use.setListPage();
  const setStoredRowsPerPage = useServiceHistoryStore.use.setListRowsPerPage();
  const applyFilter = useServiceHistoryStore.use.applyFilter();
  const currentCustomer = useSessionStore.use.currentCustomer();

  const shouldUseCachedData = React.useRef<boolean>(fromDetail);
  const [isLoading, setIsLoading] = React.useState(false);
  const [rowFilter, setRowFilter] = React.useState<RouteStopListRequest>(fromDetail ? tableState.filter : initialTableState.filter);
  const [page, setPage] = React.useState<number>(fromDetail ? tableState.page : initialTableState.page);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(fromDetail ? tableState.rowsPerPage : initialTableState.rowsPerPage);
  const [rows, setRows] = React.useState<RouteStopListItem[]>(fromDetail ? tableState.rows : []);

  useEffect(() => {
    if (!shouldUseCachedData.current || rows.length === 0) {
      setRows([]);
      setIsLoading(true);
      setPage(0);
      applyFilter(rowFilter)
        .then((rows) => {
          setRows(rows);
        })
        .finally(() => {
          shouldUseCachedData.current = false;
          setIsLoading(false);
        });
    }
    shouldUseCachedData.current = false;
  }, [rowFilter, currentCustomer]);

  const handleFilterChanged = (newFilter: RouteStopListRequest) => {
    setPage(0);
    setStoredPage(0);
    setRowFilter(newFilter);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
    setStoredPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChangePage(null, 0);
    const rowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(rowsPerPage);
    setStoredRowsPerPage(rowsPerPage);
  };

  return (
    <Paper>
      <ServiceHistoryFilterBar rowFilter={rowFilter} onFilterChanged={handleFilterChanged}></ServiceHistoryFilterBar>
      <Divider sx={{ mt: 3, mb: 0 }}></Divider>
      <Grid container sx={{ px: 2, pt: 2, pb: 0 }}>
        {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
          <Grid key={`ServiceHistory_${index}`} item xs={12} md={6}>
            <ServiceHistoryCard row={row} key={`ServiceHistory_${index}`} />
          </Grid>
        ))}
      </Grid>
      <EmptyResults pb={2} pt={2} px={4} hasRows={rows?.length > 0} isLoading={isLoading} showDefaultIcon={false} />
      <Divider sx={{ mt: 2, mb: 0 }}></Divider>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows/page:"
      />
    </Paper>
  );
}

export default ServiceHistoryCardList;
